/** Branch JSON `max_pos_manual_discount_percent`. Missing/null = unlimited. 0 = no manual discount. */

export const MAX_POS_MANUAL_DISCOUNT_PERCENT_KEY = 'max_pos_manual_discount_percent' as const

export function roundMoney2(value: number): number {
  if (!Number.isFinite(value)) return 0
  return Math.round(value * 100) / 100
}

export function normalizeMaxPosManualDiscountPercent(raw: unknown): number | null {
  if (raw === undefined || raw === null || raw === '') return null
  const n = typeof raw === 'number' ? raw : Number(raw)
  if (!Number.isFinite(n)) return null
  if (n < 0) return null
  return Math.min(100, roundMoney2(n))
}

const ARABIC_INDIC_DIGITS = '٠١٢٣٤٥٦٧٨٩'
const EASTERN_ARABIC_DIGITS = '۰۱۲۳۴۵۶۷۸۹'

function mapDigitChar(ch: string): string {
  const indic = ARABIC_INDIC_DIGITS.indexOf(ch)
  if (indic >= 0) return String(indic)
  const eastern = EASTERN_ARABIC_DIGITS.indexOf(ch)
  if (eastern >= 0) return String(eastern)
  return ch
}

/** Keep digits and at most one decimal (2 places). Drops e/+/−/letters that `type=number` lets through. */
export function sanitizeMaxPosManualDiscountPercentInput(raw: string): string {
  let out = ''
  let seenDot = false
  let decimals = 0
  for (const ch of raw) {
    const mapped = mapDigitChar(ch)
    if (mapped >= '0' && mapped <= '9') {
      if (seenDot) {
        if (decimals >= 2) continue
        decimals += 1
      }
      out += mapped
      continue
    }
    if ((mapped === '.' || mapped === ',') && !seenDot) {
      seenDot = true
      if (out === '') out = '0'
      out += '.'
    }
  }
  return out
}

function numericCapInput(raw: string): string {
  return sanitizeMaxPosManualDiscountPercentInput(raw).replace(/\.$/, '')
}

/** Empty / whitespace → null (Unlimited). Never coerce blank to 0. */
export function parseMaxPosManualDiscountPercentInput(raw: string): number | null {
  const trimmed = numericCapInput(raw)
  if (trimmed === '') return raw.trim() === '' ? null : Number.NaN
  const n = Number(trimmed)
  if (!Number.isFinite(n)) return Number.NaN
  return n
}

export function formatMaxPosManualDiscountPercentInput(raw: unknown): string {
  const n = normalizeMaxPosManualDiscountPercent(raw)
  if (n === null) return ''
  return String(n)
}

export function isMaxPosManualDiscountPercentInputValid(raw: string): boolean {
  const trimmed = raw.trim()
  if (trimmed === '') return true
  const compact = Array.from(trimmed).map(mapDigitChar).join('').replace(',', '.')
  if (!/^\d+(\.\d{0,2})?$/.test(compact)) return false
  const parsed = Number(compact.replace(/\.$/, ''))
  if (!Number.isFinite(parsed)) return false
  return parsed >= 0 && parsed <= 100
}

export function maxManualDiscountAmount(eligibleBase: number, capPercent: number | null): number | null {
  if (capPercent === null) return null
  if (!Number.isFinite(eligibleBase) || eligibleBase <= 0) return 0
  if (capPercent <= 0) return 0
  return roundMoney2((eligibleBase * Math.min(100, capPercent)) / 100)
}

export function remainingInvoiceManualDiscount(input: {
  eligibleBase: number
  capPercent: number | null
  lineManualDiscount: number
}): number | null {
  const max = maxManualDiscountAmount(input.eligibleBase, input.capPercent)
  if (max === null) return null
  return Math.max(0, roundMoney2(max - Math.max(0, input.lineManualDiscount)))
}

export function combinedManualDiscountExceedsCap(input: {
  eligibleBase: number
  capPercent: number | null
  lineManualDiscount: number
  invoiceManualDiscount: number
}): boolean {
  const max = maxManualDiscountAmount(input.eligibleBase, input.capPercent)
  if (max === null) return false
  const combined = roundMoney2(Math.max(0, input.lineManualDiscount) + Math.max(0, input.invoiceManualDiscount))
  if (input.capPercent !== null && input.capPercent <= 0) return combined > 0
  return combined > max + 1e-9
}
